import { FormWizardTemplate, FormWizardTitle } from "@iventis/components";
import { createInitialState, findNode, TreeBrowserDomainNodeContext, UnionOfNodes, useTreeBrowserInitialise } from "@iventis/tree-browser";
import React, { FunctionComponent, useMemo } from "react";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Body2, inlineTextIconMargin, styled } from "@iventis/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { canEdit } from "@iventis/utilities";
import { useProjectLimiter } from "@iventis/permissions/src/project-limiter-hook";
import { getSpatialLibraries } from "../services/libraries";
import { RootState } from "../state/root.store";
import { libraryNodeServices } from "../services/library-api-functions";
import { libraryWebSockets } from "./library.machine.web-sockets";
import { getSort } from "./library-get-initial-values";
import { EmbeddedLibraryComponent, EmbeddedLibraryModal, StyledLibraryContainer } from "./embedded-library";
import { libraryNodeTypeFontawesome } from "./library-nodes";

/**
 * Returns a new location through onConfirm, which returns the nodesToMove if provided and a new parentId
 */
export const LibraryMoveDialog: FunctionComponent<{
    closeModal: () => void;
    isOpen: boolean;
    treeId: string;
    onConfirm: (newTree: Node, nodes: UnionOfNodes<Node>[], newParentId: string) => void;
    onConfirmSwitchLibrary: (newTree: Node, newParentId: string, nodesThatHaveMoved: Node[]) => void;
    nodesToMove?: Node[];
    canSwitchLibrary: boolean;
}> = ({ closeModal, isOpen, treeId, onConfirm, onConfirmSwitchLibrary, nodesToMove = [], canSwitchLibrary }) => {
    const translate = useIventisTranslate();
    const auth = useSelector((s: RootState) => s.auth);

    const { data: libraryNodes } = useQuery(["libraryNodes"], async () => getSpatialLibraries(), {
        initialData: [],
    });

    const [treeBrowserState, treeBrowserContext] = useTreeBrowserInitialise(
        createInitialState<Node>({ sort: getSort(auth.user.userId), treeId, singleSelectionOnly: true, selectedNodeIds: [treeId] }),
        libraryNodeServices,
        libraryWebSockets
    );

    const canSelectNode = (node: Node) => {
        if (node.type !== NodeType.Folder) {
            return { disabled: true, tooltip: translate(Content.map5.move.tooltip[node.type]) };
        }
        if (nodesToMove.some((n) => n.id === node.id)) {
            return { disabled: true, tooltip: translate(Content.map5.move.tooltip_self) };
        }
        if (!canEdit(node.authorisation)) {
            return { disabled: true };
        }
        return { disabled: false, tooltip: undefined };
    };

    const header = () => {
        if (nodesToMove?.length && nodesToMove?.length >= 1) {
            return (
                <TextIconContainer>
                    {nodesToMove?.length === 1 && <FontAwesomeIcon icon={libraryNodeTypeFontawesome[nodesToMove[0].type]} />}
                    {nodesToMove?.length > 1 ? <span>{translate(Content.library2.move_node.multiple_items)}</span> : <Body2>{nodesToMove[0]?.name}</Body2>}
                </TextIconContainer>
            );
        }
        return undefined;
    };

    const title = () => {
        if (nodesToMove?.length && nodesToMove?.length >= 1) {
            return nodesToMove?.length === 1
                ? translate(Content.library2.move_node.title, { nodeType: nodesToMove[0].type })
                : translate(Content.library2.move_node.multi_title, { count: nodesToMove.length });
        }
        return translate(Content.library2.move_node.empty_title);
    };

    const isSharedLibrary = treeBrowserState?.tree?.type === NodeType.MappingLibrary;

    const [canUse] = useProjectLimiter();

    const reachedTeamMapLimit = isSharedLibrary && !canUse("teamMaps");

    const displayTree = useMemo(() => findNode([treeBrowserState.tree], treeBrowserState.mainNodeId), [treeBrowserState.tree, treeBrowserState.mainNodeId]);

    const { isLoadingTree, tree, selectedNodeIds } = treeBrowserState;
    const permissionToEditFolder = useMemo(() => {
        const selectedNode = findNode([tree], selectedNodeIds[0]);
        const selectedNodeAuthorisation = selectedNode?.authorisation;
        const displayTreeAuthorisation = displayTree?.authorisation;
        return treeBrowserState.selectedNodeIds.length > 0 ? canEdit(selectedNodeAuthorisation) : canEdit(displayTreeAuthorisation);
    }, [selectedNodeIds]);

    const canMoveToLocation = !reachedTeamMapLimit && !isLoadingTree && permissionToEditFolder;

    return (
        <EmbeddedLibraryModal open={isOpen} close={closeModal} testid="library-move-modal">
            <FormWizardTemplate
                title={<FormWizardTitle header={header()} title={title()} />}
                currentStage={0}
                stages={[
                    {
                        isValid: canMoveToLocation,
                        disabledSubmitButtonText: translate(Content.library2.move_node.no_permission_tooltip),
                        primaryButtonText: nodesToMove?.length >= 1 ? translate(Content.map5.move.moveTo) : translate(Content.map5.move.setTo),
                        primaryButtonCallback: () => {
                            const moveToId = treeBrowserState.selectedNodeIds?.[0] ?? treeBrowserState.mainNodeId;

                            // If the modal treeId is the same as the current treeId, we can move locally
                            if (treeBrowserState.treeId === treeId) {
                                onConfirm(treeBrowserContext.currentState.tree, nodesToMove, treeBrowserState.selectedNodeIds?.[0] ?? treeBrowserState.mainNodeId);
                            } else {
                                // Otherwise, move it within this modal context
                                treeBrowserContext.moveNodes([...nodesToMove], moveToId);
                                onConfirmSwitchLibrary(treeBrowserContext.currentState.tree, moveToId, nodesToMove);
                            }
                            closeModal();
                        },
                        secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: closeModal }],
                    },
                ]}
            >
                <TreeBrowserDomainNodeContext.Provider value={treeBrowserContext}>
                    <StyledLibraryContainer>
                        {treeBrowserContext != null && (
                            <EmbeddedLibraryComponent
                                canSwitchLibrary={canSwitchLibrary}
                                onLibrarySelectionChange={(node) => {
                                    treeBrowserContext.setTree(node);
                                }}
                                libraries={libraryNodes}
                                userId={auth.user.userId}
                                canSelectNode={canSelectNode}
                                nodesBeingEdited={nodesToMove.map((n) => n.id)}
                            />
                        )}
                    </StyledLibraryContainer>
                </TreeBrowserDomainNodeContext.Provider>
            </FormWizardTemplate>
        </EmbeddedLibraryModal>
    );
};

const TextIconContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${inlineTextIconMargin};
    color: ${(props) => props.theme.shades.two};
`;
